import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Menu, X, Sun, Moon, Award, ChevronDown,
  Home as HomeIcon, Layers, Package, Landmark, CheckCircle, Shield,
  Globe, FileText, Trophy, Wrench, Users, BookOpen, Info, Phone, Lock
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/Button";
import { Logo } from "../ui/Logo";
import { cn } from "../../utils/cn";
import { useTheme } from "../../context/ThemeContext";
import api from "../../services/api";

export function Navbar() {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  
  const primaryLinks = [
    { name: "Home", href: "/" },
    { name: "Services", href: "/services" },
    { name: "Packages", href: "/packages" },
    { name: "MSME Loan", href: "/funding" },
  ];

  const resourceLinks = [
    { name: "Loans Check", href: "/loan-eligibility" },
    { name: "Business Insurance", href: "/insurance" },
    { name: "Export Hub", href: "/export/dashboard" },
    { name: "Tenders Aggregator", href: "/tenders" },
    { name: "Leaderboard", href: "/leaderboard" },
    { name: "Tools & Calculators", href: "/tools" },
    { name: "Privacy Dashboard", href: "/privacy" },
    { name: "Blog", href: "/blog" },
    { name: "Community", href: "/community" },
    { name: "About Us", href: "/about" },
    { name: "Contact Us", href: "/contact" },
  ];

  const mobileNavGroups = [
    {
      title: null,
      items: [
        { name: "Home", href: "/", icon: HomeIcon },
        { name: "Services", href: "/services", icon: Layers },
        { name: "Packages", href: "/packages", icon: Package },
        { name: "MSME Loan", href: "/funding", icon: Landmark },
      ],
    },
    {
      title: "Resources & Tools",
      items: [
        { name: "Loans Check", href: "/loan-eligibility", icon: CheckCircle },
        { name: "Business Insurance", href: "/insurance", icon: Shield },
        { name: "Export Hub", href: "/export/dashboard", icon: Globe },
        { name: "Tenders Portal", href: "/tenders", icon: FileText },
        { name: "Leaderboard", href: "/leaderboard", icon: Trophy },
        { name: "Tools & Calculators", href: "/tools", icon: Wrench },
        { name: "Community", href: "/community", icon: Users },
        { name: "Blog", href: "/blog", icon: BookOpen },
      ],
    },
    {
      title: "Company",
      items: [
        { name: "About Us", href: "/about", icon: Info },
        { name: "Contact Us", href: "/contact", icon: Phone },
        { name: "Privacy Dashboard", href: "/privacy", icon: Lock },
      ],
    },
  ];

  const mobileLegalLinks = [
    { name: "Privacy Policy", href: "/privacy-policy" },
    { name: "Terms of Service", href: "/terms" },
    { name: "Refund Policy", href: "/refund-policy" },
  ];

  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (typeof window !== 'undefined') {
      return !!localStorage.getItem('accessToken');
    }
    return false;
  });
  const [isAdmin, setIsAdmin] = useState(() => {
    if (typeof window !== 'undefined') {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user?.role === 'admin' || user?.role === 'super_admin';
    }
    return false;
  });

  const [points, setPoints] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      api.get('/api/gamification/me')
        .then(res => setPoints(res.data.points || 0))
        .catch(err => console.error('Error loading points in navbar:', err));
    }
  }, [isAuthenticated]);

  const handleLogout = async () => {
    try {
      await api.post('/api/auth/logout');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      setIsAuthenticated(false);
      setIsAdmin(false);
      toast.success('Logged out successfully');
      window.location.href = '/login';
    } catch {
      toast.error('Logout failed');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      setIsAuthenticated(false);
      setIsAdmin(false);
      window.location.href = '/login';
    }
  };

  return (
    <header className={cn(
      "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
      scrolled
        ? "bg-white/60 dark:bg-[#0A1628]/60 backdrop-blur-xl saturate-150 border-b border-slate-200/70 dark:border-white/10 shadow-sm dark:shadow-[0_4px_30px_rgba(0,0,0,0.5)]"
        : "bg-transparent border-b border-transparent"
    )}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center group">
            <Logo size="md" />
          </Link>
          
          {/* Desktop Nav Items */}
          <nav className="hidden lg:flex items-center space-x-1">
            {primaryLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="px-4 py-2 rounded-full text-sm font-semibold text-slate-600 dark:text-white/70 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 transition-all"
              >
                {link.name}
              </Link>
            ))}

            {/* Resources Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <button 
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 px-4 py-2 rounded-full text-sm font-semibold text-slate-600 dark:text-white/70 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 transition-all"
              >
                <span>Resources</span>
                <ChevronDown className={cn("w-4 h-4 transition-transform duration-200", dropdownOpen && "rotate-180")} />
              </button>
              
              {dropdownOpen && (
                <div className="absolute top-full left-0 pt-2 z-50">
                  <div className="w-52 bg-white/95 dark:bg-[#0A1628]/95 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-2xl shadow-xl overflow-hidden py-2">
                    {resourceLinks.map(link => (
                      <Link
                        key={link.name}
                        to={link.href}
                        onClick={() => setDropdownOpen(false)}
                        className="block px-4 py-2.5 text-sm font-semibold text-slate-600 dark:text-white/70 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 transition-all"
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </nav>

          {/* Desktop Authenticated & Custom Panel */}
          <div className="hidden lg:flex items-center space-x-3 ml-6">
            {isAuthenticated ? (
              <div className="flex items-center space-x-3">
                <Link to="/rewards" className="flex items-center gap-1.5 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 px-3.5 py-1.5 rounded-full border border-cyan-500/20 text-xs font-black transition-all">
                  <Award className="w-3.5 h-3.5" />
                  <span>{points.toLocaleString()} XP</span>
                </Link>
                <Link to="/dashboard">
                  <Button variant="ghost" className="text-slate-700 dark:text-white/90 hover:bg-black/5 dark:hover:bg-white/10 dark:bg-white/5 rounded-full font-semibold">Dashboard</Button>
                </Link>
                {isAdmin && (
                  <Link to="/admin">
                    <Button variant="ghost" className="text-slate-700 dark:text-white/90 hover:bg-black/5 dark:hover:bg-white/10 dark:bg-white/5 rounded-full font-semibold">Admin</Button>
                  </Link>
                )}
                <Button variant="outline" onClick={handleLogout} className="border-gray-300 dark:border-white/10 text-slate-700 dark:text-white/90 hover:bg-gray-100 dark:hover:bg-white/10 dark:bg-white/5 rounded-full font-semibold">
                  Logout
                </Button>
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 dark:bg-white/5 transition-colors"
                  aria-label="Toggle theme"
                >
                  {theme === 'light' ? <Moon className="w-5 h-5 text-gray-700" /> : <Sun className="w-5 h-5 text-yellow-400" />}
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Link to="/login" className="text-sm font-semibold text-gray-600 dark:text-white/70 hover:text-slate-900 dark:hover:text-white transition-colors">
                  Login
                </Link>
                <Link to="/register">
                  <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white rounded-full font-bold shadow-lg transition-all border-none">
                    Get Started
                  </Button>
                </Link>
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 dark:bg-white/5 transition-colors"
                  aria-label="Toggle theme"
                >
                  {theme === 'light' ? <Moon className="w-5 h-5 text-gray-700" /> : <Sun className="w-5 h-5 text-yellow-400" />}
                </button>
              </div>
            )}
          </div>
          
          {/* Mobile Display Header Right Items */}
          <div className="flex lg:hidden items-center space-x-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? <Moon className="w-5 h-5 text-gray-700" /> : <Sun className="w-5 h-5 text-yellow-400" />}
            </button>
            <button
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-slate-700 dark:text-white transition-colors"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer menu */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="lg:hidden bg-white/95 dark:bg-[#0A1628]/95 backdrop-blur-xl border-t border-slate-200 dark:border-white/10 mt-2 rounded-2xl shadow-lg dark:shadow-2xl max-h-[calc(100vh-5rem)] overflow-y-auto"
        >
          <div className="px-3 py-4">
            {mobileNavGroups.map((group, gi) => (
              <div key={group.title || 'main'} className={gi > 0 ? "mt-2 pt-3 border-t border-gray-100 dark:border-white/5" : ""}>
                {group.title && (
                  <p className="px-3 pb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-white/30">{group.title}</p>
                )}
                <div className="space-y-0.5">
                  {group.items.map(link => {
                    const isActive = location.pathname === link.href;
                    return (
                      <Link
                        key={link.name}
                        to={link.href}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors",
                          isActive
                            ? "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400"
                            : "text-slate-600 dark:text-white/70 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10"
                        )}
                        onClick={() => setIsOpen(false)}
                      >
                        <link.icon className="w-4 h-4 flex-shrink-0" />
                        {link.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="pt-4 pb-3 mt-2 border-t border-gray-200 dark:border-white/10 px-3 space-y-3">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/rewards"
                    className="flex items-center justify-center gap-1.5 w-full bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 px-3.5 py-2.5 rounded-full border border-cyan-500/20 text-xs font-black transition-all"
                    onClick={() => setIsOpen(false)}
                  >
                    <Award className="w-3.5 h-3.5" />
                    <span>{points.toLocaleString()} XP</span>
                  </Link>
                  <Link to="/dashboard" className="block w-full" onClick={() => setIsOpen(false)}>
                    <Button variant="ghost" size="sm" className="w-full text-slate-700 dark:text-white/90 hover:bg-black/5 dark:hover:bg-white/10 dark:bg-white/5 rounded-full font-semibold">Dashboard</Button>
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" className="block w-full" onClick={() => setIsOpen(false)}>
                      <Button variant="ghost" size="sm" className="w-full text-slate-700 dark:text-white/90 hover:bg-black/5 dark:hover:bg-white/10 dark:bg-white/5 rounded-full font-semibold">Admin</Button>
                    </Link>
                  )}
                  <Button variant="outline" size="sm" className="w-full text-slate-700 dark:text-white/90 border-gray-300 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/10 dark:bg-white/5 rounded-full font-semibold" onClick={handleLogout}>Logout</Button>
                </>
              ) : (
                <>
                  <Link to="/login" className="block w-full" onClick={() => setIsOpen(false)}>
                    <Button variant="ghost" size="sm" className="w-full text-slate-700 dark:text-white/90 hover:bg-black/5 dark:hover:bg-white/10 dark:bg-white/5 rounded-full font-semibold">Login</Button>
                  </Link>
                  <Link to="/register" className="block w-full" onClick={() => setIsOpen(false)}>
                    <Button className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-full font-bold shadow-md">Register</Button>
                  </Link>
                </>
              )}
            </div>
            <div className="pt-4 border-t border-gray-200 dark:border-white/10 px-3 flex flex-wrap gap-x-4 gap-y-2">
              {mobileLegalLinks.map(link => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-xs font-semibold text-slate-500 dark:text-white/50 hover:text-slate-900 dark:hover:text-white transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
            </div>
            <div className="pt-4 px-3 text-xs text-slate-500 dark:text-white/50 space-y-1">
              <p>80 Feet Road, Koramangala 4th Block, Bengaluru, Karnataka 560034</p>
              <p>+91 87460 59496 &middot; hello@msmecenter.com</p>
              <p className="pt-2 text-slate-400 dark:text-white/30">&copy; {new Date().getFullYear()} MSMECenter. All rights reserved.</p>
            </div>
          </div>
        </motion.div>
      )}
    </header>
  );
}
